import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ShieldAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { api, errCode } from "@/lib/api";
import { AuthShell, GoogleIcon, startGoogleLogin } from "@/pages/Login";

export default function Register() {
  const { login } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [mode, setMode] = useState("new"); // new | invite
  const [form, setForm] = useState({ name: "", email: "", password: "", org_name: "", invite_code: "" });
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { setError(t("errors.consent_required")); return; }
    setBusy(true);
    setError("");
    try {
      const payload = { name: form.name, email: form.email, password: form.password, language: lang, consent: true };
      if (mode === "invite") payload.invite_code = form.invite_code;
      else payload.org_name = form.org_name;
      const { data } = await api.post("/auth/register", payload);
      login(data);
      navigate("/dashboard");
    } catch (err) {
      const code = errCode(err);
      setError(t(`errors.${code}`, code));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title={t("auth.registerTitle")} footer={<>{t("auth.haveAccount")} <Link to="/login" className="text-vox-bright hover:text-white" data-testid="go-login-link">{t("nav.login")}</Link></>}>
      <button type="button" onClick={startGoogleLogin} className="btn-ghost w-full" data-testid="google-register-button">
        <GoogleIcon /> {t("auth.google")}
      </button>
      <p className="mt-2 text-center text-[11px] leading-relaxed text-vox-dim" data-testid="google-consent-note">
        {t("auth.consentGoogle")} <Link to="/legal/terms" className="underline hover:text-vox-text">{t("auth.termsAcc")}</Link> {t("auth.consentAnd")} <Link to="/legal/privacy" className="underline hover:text-vox-text">{t("auth.privacyAcc")}</Link>
      </p>
      <div className="my-6 flex items-center gap-4 text-xs text-vox-dim"><span className="h-px flex-1 bg-vox-line" />{t("auth.or")}<span className="h-px flex-1 bg-vox-line" /></div>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div>
          <label className="mono-label mb-2 block" htmlFor="reg-name">{t("auth.name")}</label>
          <input id="reg-name" required className="input" value={form.name} onChange={set("name")} data-testid="register-name-input" />
        </div>
        <div>
          <label className="mono-label mb-2 block" htmlFor="reg-email">{t("auth.email")}</label>
          <input id="reg-email" type="email" required className="input" value={form.email} onChange={set("email")} data-testid="register-email-input" autoComplete="email" />
        </div>
        <div>
          <label className="mono-label mb-2 block" htmlFor="reg-password">{t("auth.password")}</label>
          <input id="reg-password" type="password" required minLength={8} className="input" value={form.password} onChange={set("password")} data-testid="register-password-input" autoComplete="new-password" />
          <p className="mt-1.5 text-xs text-vox-dim" data-testid="register-password-hint">{t("auth.passwordHint")}</p>
        </div>
        <div className="flex gap-1 rounded-full border border-vox-line bg-vox-deep/60 p-1">
          {["new", "invite"].map((m) => (
            <button key={m} type="button" onClick={() => setMode(m)} data-testid={`register-mode-${m}`}
              className={`flex-1 rounded-full px-3 py-1.5 text-xs transition-colors duration-200 ${mode === m ? "bg-vox-card text-vox-text" : "text-vox-dim hover:text-vox-text"}`}>
              {m === "new" ? t("auth.newOrg") : t("auth.haveInvite")}
            </button>
          ))}
        </div>
        {mode === "new" ? (
          <div>
            <label className="mono-label mb-2 block" htmlFor="reg-org">{t("auth.orgName")}</label>
            <input id="reg-org" className="input" value={form.org_name} onChange={set("org_name")} data-testid="register-org-input" />
          </div>
        ) : (
          <div>
            <label className="mono-label mb-2 block" htmlFor="reg-invite">{t("auth.inviteCode")}</label>
            <input id="reg-invite" required className="input font-mono uppercase" value={form.invite_code} onChange={set("invite_code")} data-testid="register-invite-input" />
          </div>
        )}
        <div className="flex gap-3 rounded-xl border border-vox-amber/30 bg-vox-amber/10 p-4" data-testid="password-no-recovery-warning">
          <ShieldAlert size={18} className="mt-0.5 shrink-0 text-vox-amber" />
          <div>
            <div className="text-sm font-medium text-vox-amber">{t("auth.noRecoveryTitle")}</div>
            <p className="mt-1 text-xs leading-relaxed text-vox-muted">{t("auth.noRecovery")}</p>
          </div>
        </div>
        <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-vox-muted" data-testid="consent-label">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-[#94BFC7]" data-testid="register-consent-checkbox" />
          <span>
            {t("auth.consent")} <Link to="/legal/terms" className="text-vox-bright underline hover:text-white" data-testid="consent-terms-link">{t("auth.termsAcc")}</Link> {t("auth.consentAnd")} <Link to="/legal/privacy" className="text-vox-bright underline hover:text-white" data-testid="consent-privacy-link">{t("auth.privacyAcc")}</Link>{t("auth.consentTail")}
          </span>
        </label>
        <p className="text-xs leading-relaxed text-vox-dim">{t("auth.freeNote")}</p>
        {error && <div className="rounded-xl border border-vox-rec/30 bg-vox-rec/10 px-4 py-3 text-sm text-[#ff9b9e]" data-testid="register-error">{error}</div>}
        <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="register-submit-button">
          {busy ? <Loader2 size={16} className="animate-spin" /> : t("auth.submitRegister")}
        </button>
      </form>
    </AuthShell>
  );
}
